import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Band {
  id: string;
  nome: string;
  cidade: string;
  imagem_url?: string;
  biografia?: string;
  instagram?: string;
  facebook?: string;
  tiktok?: string;
  youtube?: string;
  spotify_link?: string;
  musica_youtube?: string;
  user_id?: string;
  estilo_musical?: 'bailao' | 'gaucho_vaneira' | 'germanica' | null;
  created_at: string;
  telefone?: string;
  email?: string;
  slug?: string;
}

export interface BandFormData {
  nome: string;
  cidade: string;
  biografia?: string;
  instagram?: string;
  facebook?: string;
  tiktok?: string;
  youtube?: string;
  spotify_link?: string;
  musica_youtube?: string;
  imagem_url?: string;
  estilo_musical?: 'bailao' | 'gaucho_vaneira' | 'germanica' | null;
  telefone?: string;
  email?: string;
}

const BANDS_QUERY_KEY = ['bands'];

export const useBandsQuery = () => {
  const queryClient = useQueryClient();

  const { data: bands = [], isLoading, error } = useQuery({
    queryKey: BANDS_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_public_bands');

      if (error) throw error;
      return data as Band[];
    },
  });

  const uploadImageMutation = useMutation({
    mutationFn: async (file: File) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `bandas-imagens/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('bandas-imagens')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('bandas-imagens')
        .getPublicUrl(filePath);

      return publicUrl;
    },
  });

  const createBandMutation = useMutation({
    mutationFn: async ({ 
      formData, 
      imageFile, 
      editingId 
    }: { 
      formData: BandFormData; 
      imageFile?: File; 
      editingId?: string;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Usuário não autenticado');

      let imageUrl = formData.imagem_url;
      if (imageFile) {
        imageUrl = await uploadImageMutation.mutateAsync(imageFile);
      }

      const bandData = {
        ...formData,
        imagem_url: imageUrl,
        user_id: session.user.id,
      };

      if (editingId) {
        const { data, error } = await supabase
          .from('bandas')
          .update(bandData)
          .eq('id', editingId)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('bandas')
          .insert([bandData])
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BANDS_QUERY_KEY });
    },
  });

  return {
    bands,
    isLoading,
    error,
    createBand: createBandMutation.mutate,
    isCreating: createBandMutation.isPending,
  };
};
